import {
  Avatar,
  Checkbox,
  Grid,
  IconButton,
  Link,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Skeleton } from '@mui/material';
import React, { useContext, useState } from 'react';
import useToggle from '../../../../hooks/useToggle';
import LaunchDarklyContext from '../../../../shared-components/contexts/LaunchDarklyContext';
import {
  GetCoreUserFormattedName,
  ICoreUser,
} from '../../../../shared-interfaces/User/ICoreUser';
import { IRetentionStatus } from '../../../../shared-interfaces/User/IRetentionStatus';
import { Role, RoleToString } from '../../../../shared-types/Roles';
import { UserManagementState } from '../UserManagement';
import ChangeRetentionDialog from './ChangeRetentionStatusDialog';
import ChangeRoleDialog from './ChangeRoleDialog';
import { useNavigate } from 'react-router';
import UserContext from '../../../../shared-components/contexts/UserContext';
import { UserAttributesContext } from '../../../../shared-components/contexts/UserAttributesContext';

export interface IUserRow {
  user: ICoreUser;
  retentionStatus?: IRetentionStatus;
  selected?: boolean;
  onClick: () => void;
}

const useStyles = makeStyles({
  avatar: {
    width: '60px',
    height: '60px',
    marginRight: '16px',
  },
  row: {
    borderBottom: '1px solid #dcdcdc',
    listStyle: 'none',
  },
  name: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontWeight: 600,
  },
  gridContainer: {
    padding: '8px 0px 8px 4px',
    borderBottom: '1px solid #dcdcdc',
    maxHeight: '110px',
  },
});

const UserRow = (props: IUserRow) => {
  const classes = useStyles();

  const navigate = useNavigate();
  const state = useContext(UserManagementState);
  const user = useContext(UserContext);
  const userAttributeContext = useContext(UserAttributesContext);

  const [retentionStatusId, setRetentionStatusId] = useState(
    props.user.retentionStatusId,
  );

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [showChangeRoleDialog, toggleShowChangeRoleDialog] = useToggle();
  const [showChangeRetentionDialog, toggleShowChangeRetentionDialog] =
    useToggle();

  const handleGoToManageConnections = () => {
    try {
      navigate(`/management/users/${props.user.externalId}/connections`, {
        state: { user: props.user },
      });
    } catch {
      navigate(`/management/users/${props.user.externalId}/connections`);
    }
  };

  return (
    <li className={classes.row}>
      <ListItem ContainerComponent="div">
        <ListItemIcon>
          <Checkbox
            color="primary"
            checked={props.selected}
            inputProps={{
              'aria-label': `select ${GetCoreUserFormattedName(
                props.user,
                false,
              )}`,
            }}
            onChange={async () => props.onClick()}
          />
        </ListItemIcon>
        <ListItemAvatar>
          <Avatar
            className={classes.avatar}
            alt={GetCoreUserFormattedName(props.user, false)}
            src={
              props.user.profileImageFilePath ??
              props.user.institutionProfileImageFilePath
            }
          />
        </ListItemAvatar>
        <ListItemText
          disableTypography
          primary={
            <Link
              href={`/beacon/students/advise/student/${props.user.externalId}`}
              target="_blank"
              aria-label={`Go to ${GetCoreUserFormattedName(
                props.user,
                false,
              )} profile`}
              data-clarity-mask="true"
            >
              <Typography
                variant="h3"
                aria-label={GetCoreUserFormattedName(props.user, false)}
                className={classes.name}
                data-clarity-mask="true"
              >
                {GetCoreUserFormattedName(props.user, false)}
              </Typography>
            </Link>
          }
          secondary={
            <Typography variant="subtitle2" data-clarity-mask="true">
              {props.user.externalId}
            </Typography>
          }
        />
        <Menu
          id={`individual-actions-${props.user.id}`}
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              handleGoToManageConnections();
            }}
          >
            Manage Connections
          </MenuItem>

          {user.isAdministrator && (
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                toggleShowChangeRoleDialog();
              }}
            >
              Change Role
            </MenuItem>
          )}
          {user.isAdministrator && (
            <Tooltip
              title="Change Retention Status is only applicable to users with the Students or Peer Advisor roles"
              disableHoverListener={props.user.role <= Role.PeerAdvisor}
              arrow
            >
              <span>
                <MenuItem
                  onClick={() => {
                    setAnchorEl(null);
                    toggleShowChangeRetentionDialog();
                  }}
                  disabled={props.user.role > Role.PeerAdvisor}
                >
                  Change Retention Status
                </MenuItem>
              </span>
            </Tooltip>
          )}
        </Menu>

        <ListItemSecondaryAction>
          <IconButton
            aria-label="more actions"
            aria-controls={`individual-actions-${props.user.id}`}
            aria-haspopup="true"
            onClick={e => setAnchorEl(e.currentTarget)}
            size="large"
          >
            <MoreVertIcon />
          </IconButton>
        </ListItemSecondaryAction>
      </ListItem>
      <div style={{ padding: '0 0 15px 148px' }}>
        <Grid container direction="row" style={{ maxHeight: '70px' }}>
          <Grid item xs={4}>
            <Typography variant="caption">
              <strong>Role:</strong> {RoleToString(props.user.role)}
            </Typography>
            <br />
            {(props.user.role === Role.Student ||
              props.user.role === Role.PeerAdvisor) && (
              <Typography variant="caption">
                <strong>Retention Status:</strong>{' '}
                {props.user.retentionStatusId
                  ? userAttributeContext.retentionStatuses.find(
                      r => r.id === retentionStatusId,
                    )?.name
                  : 'none'}
              </Typography>
            )}
          </Grid>
          <Grid item xs={2}>
            {props.user.role === Role.Student && (
              <Typography variant="caption">
                <strong>Advisors:</strong> {props.user.advisorCount}
              </Typography>
            )}
            {props.user.role === Role.PeerAdvisor && (
              <>
                <Typography variant="caption">
                  <strong>Advisors:</strong> {props.user.advisorCount}
                </Typography>
                <br />
                <Typography variant="caption">
                  <strong>Advisees:</strong> {props.user.adviseeCount}
                </Typography>
              </>
            )}
            {props.user.role > Role.PeerAdvisor && (
              <Typography variant="caption">
                <strong>Advisees:</strong> {props.user.adviseeCount}
              </Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            {props.user.primaryEmailAddress && (
              <>
                <Typography variant="caption" data-clarity-mask="true">
                  <strong>Primary Email:</strong>{' '}
                  {props.user.primaryEmailAddress}
                </Typography>
                <br />
              </>
            )}
            {props.user.preferredEmailAddress && (
              <Typography variant="caption" data-clarity-mask="true">
                <strong>Preferred Email:</strong>{' '}
                {props.user.preferredEmailAddress}
              </Typography>
            )}
          </Grid>
        </Grid>
      </div>

      <ChangeRoleDialog
        open={showChangeRoleDialog}
        close={toggleShowChangeRoleDialog}
        user={props.user}
        entryMethod="UserList"
      />
      <ChangeRetentionDialog
        open={showChangeRetentionDialog}
        close={toggleShowChangeRetentionDialog}
        user={props.user}
        entryMethod="UserList"
        onChange={r => {
          setRetentionStatusId(r.id);
        }}
      />
    </li>
  );
};

export const UserRowLoading = () => {
  const classes = useStyles();
  const launchDarkly = useContext(LaunchDarklyContext);

  return (
    <li aria-busy="true" className={classes.row}>
      <ListItem ContainerComponent="div">
        <ListItemIcon>
          <Skeleton
            variant="rectangular"
            width={20}
            height={20}
            style={{ marginLeft: '11px' }}
          />
        </ListItemIcon>
        <ListItemAvatar>
          <Skeleton variant="circular" className={classes.avatar} />
        </ListItemAvatar>
        <ListItemText
          disableTypography
          primary={<Skeleton variant="text" width={300} />}
          secondary={<Skeleton variant="text" width={150} />}
        />
        <ListItemSecondaryAction>
          <Skeleton variant="rectangular" width={10} height={25} />
        </ListItemSecondaryAction>
      </ListItem>
      <div style={{ padding: '0 0 15px 148px' }}>
        <Grid container direction="row" style={{ maxHeight: '70px' }}>
          <Grid item xs={4}>
            <Skeleton variant="text" width={200} />
            <Skeleton variant="text" width={200} />
          </Grid>
          <Grid item xs={2}>
            <Skeleton variant="text" width={100} />
            <Skeleton variant="text" width={100} />
          </Grid>
          <Grid item xs={6}>
            <Skeleton variant="text" width={200} />
            <Skeleton variant="text" width={200} />
          </Grid>
        </Grid>
      </div>
    </li>
  );
};

export default UserRow;
